import { Money, MoneyCurrency } from '@booking-core/domain/value_objects/money.js';
import { InvariantViolation } from '@shared-kernel/domain-error.js';
import type {
  PricingInput,
  PricingStrategy,
} from '../pricing-strategy.js';
import {
  findTierFor,
  validateAndSortTiers,
  type PartyTier,
} from '../../value_objects/pricing-tier.js';

export interface TieredPerPersonParams {
  readonly tiers: readonly PartyTier[];
}

/**
 * 階梯式每人每小時。不同 party-size 帶不同 per-person-per-hour 價。
 * Tier 驗證（非空 / 非重疊 / 連續 / 只最後一層可 open-end）委派給
 * `@pricing/.../pricing-tier.ts` 的 SSOT — 舊 booking_core 跟 play_session
 * 各 validate 一次的重複從此消失。
 */
export class TieredPerPersonStrategy implements PricingStrategy {
  private readonly sortedTiers: readonly PartyTier[];

  constructor(params: TieredPerPersonParams) {
    this.sortedTiers = validateAndSortTiers(params.tiers);
  }

  quote(input: PricingInput): Money {
    if (input.partySize <= 0) {
      return Money.zero(MoneyCurrency.TWD);
    }
    const matched = findTierFor(this.sortedTiers, input.partySize);
    if (!matched) {
      throw new InvariantViolation(
        `TieredPerPersonStrategy: no tier covers partySize=${input.partySize}`,
      );
    }
    const raw =
      matched.amountPerPersonPerHour *
      (input.billableDurationMinutes / 60) *
      input.partySize;
    return Money.of({ amount: Math.round(raw), currency: MoneyCurrency.TWD });
  }
}
